import { Button, Form, Input, Modal, Tabs, Upload, notification } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { ChangeEvent, MouseEvent, PureComponent } from 'react';
import { randomString } from '../../../../store/utils';
import { getDoc, getPatientByLocalId } from '../../../../store/idbHandler';
import { Patient } from '../../../../store/types/patient';
import uploadAttachment from '../../../../api/uploadAttachment';
import { track } from '../../../../tracking';
import AttachmentTemplateList from './AttachmentTemplateList';
import getAttachmentTemplatesForDoctor, {
    Template,
} from '../../../../api/getAttachmentTemplatesForDoctor';
import deleteAttachment from '../../../../api/deleteAttachmentTemplateById';
import { Attachment } from '../../../../store/types/prescription';
const { TabPane } = Tabs;

interface AttachmentModalProps {
    isVisible?: boolean;
    onVisibleChange: (visible: boolean) => void;
    patientId?: string;
    onSend: (attachment: Attachment) => void;
    templateApplyText: string;
    templateApplyIcon?: JSX.Element;
    title?: string;
    description?: string;
    link?: string;
    file_type?: string;
    onClear?: () => void;
    tabBarExtraContent?: JSX.Element;
}

export class AttachmentModal extends PureComponent<AttachmentModalProps> {
    onCancel = (e?: MouseEvent<HTMLElement, globalThis.MouseEvent>) => {
        const { onVisibleChange } = this.props;
        e?.stopPropagation();
        onVisibleChange(false);
        track('ATTACHMENT_CANCEL_CLICK');
    };

    render() {
        const {
            isVisible,
            patientId,
            onVisibleChange,
            onSend,
            templateApplyText,
            templateApplyIcon,
            title,
            description,
            link,
            file_type,
            onClear,
            tabBarExtraContent,
        } = this.props;

        return (
            <Modal
                onCancel={this.onCancel}
                width={600}
                title=""
                closable={false}
                visible={isVisible}
                destroyOnClose
                footer={null}
                bodyStyle={{
                    padding: '0px',
                    borderRadius: '4px !important',
                    height: '500px',
                }}
            >
                <Attachments
                    patientId={patientId}
                    isVisible={isVisible}
                    onVisibleChange={onVisibleChange}
                    onCancel={this.onCancel}
                    onSend={onSend}
                    templateApplyText={templateApplyText}
                    templateApplyIcon={templateApplyIcon}
                    title={title}
                    description={description}
                    link={link}
                    file_type={file_type}
                    onClear={onClear}
                    tabBarExtraContent={tabBarExtraContent}
                />

                <style>
                    {`
                      .select-container  .ant-form-item-control-wrapper {
                        width: 100%;
                      }

                      .attachment-tabs .ant-tabs-content {
                        height: 100%;
                      }

                      .ant-form-item {
                        margin-bottom: 0px !important;
                      }
                    `}
                </style>
            </Modal>
        );
    }
}

type State = {
    title: string;
    description: string;
    link: string;
    patient?: Patient;
    activeTab: 'files' | 'template';
    uploadState: 'uploading' | 'uploaded' | '';
    file_type?: string;
    titleError?: string;
    showAttachments?: boolean;
    templates: Template[];
};

type Props = {
    isVisible?: boolean;
    onVisibleChange: (visible: boolean) => void;
    patientId?: string;
    onCancel: (e?: MouseEvent<HTMLElement, globalThis.MouseEvent> | undefined) => void;
    onClear?: () => void;
    onSend: (attachment: Attachment) => void;
    templateApplyText: string;
    templateApplyIcon?: JSX.Element;
    title?: string;
    description?: string;
    link?: string;
    file_type?: string;
    tabBarExtraContent?: JSX.Element;
};

class Attachments extends PureComponent<Props, State> {
    docId = getDoc()?._id;
    fileId = `file=${randomString(8)}`;

    state: State = {
        activeTab: 'files',
        title: this.props.title || '',
        description: this.props.description || '',
        link: this.props.link || '',
        uploadState: '',
        file_type: this.props.file_type || '',
        showAttachments: false,
        templates: [],
    };

    componentDidMount = async () => {
        try {
            const [patient, attachmentTemplate] = await Promise.all([
                getPatientByLocalId(this.props?.patientId || ''),
                getAttachmentTemplatesForDoctor(),
            ]);
            this.setState({ patient: patient, templates: attachmentTemplate });
        } catch (error) {
            console.error(error);
        }
    };

    onFileChange = (file: File) => {
        this.setState({
            uploadState: 'uploading',
        });

        const formData = new FormData();
        formData.append('attachment', file);
        uploadAttachment(formData)
            .then((res) => {
                this.setState({
                    link: res?.url || '',
                    file_type: file?.type || '',
                });

                this.setState({
                    uploadState: 'uploaded',
                });
                track('ATTACHMENT_FILE_UPLOADED');
            })
            .catch(() => {
                notification.error({
                    message: 'Failed to upload file',
                    duration: 5,
                });
                this.setState({
                    uploadState: '',
                });
                track('ATTACHMENT_FILE_UPLOAD_FAILED');
            });
    };

    onSendAttachment = async (
        title: string = '',
        description: string = '',
        link: string = '',
        file_type: string = '',
        templateId?: string,
    ) => {
        track('SEND_ATTACHMENT_CLICK');

        this.props.onSend({
            title: title,
            description: description,
            link: link,
            file_type: file_type,
            templateId: templateId,
        });
    };

    onSendClick = (e: MouseEvent<HTMLButtonElement>) => {
        const { title = '', description = '', link = '', file_type = '', patient } = this.state;
        const { onVisibleChange } = this.props;

        this.onSendAttachment(title, description, link, file_type);

        e?.stopPropagation();
        onVisibleChange(false);
    };

    onSendTemplateClick = (templates: Template) => {
        const { title = '', description = '', link = '', file_type = '' } = templates.meta.display;
        const { patient } = this.state;

        const { onVisibleChange } = this.props;

        this.onSendAttachment(title, description, link, file_type, templates?._id);

        onVisibleChange(false);
    };

    onChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ title: e.target.value });
    };

    onChangeDescription = (e: ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ description: e.target.value });
    };

    onShowAttachment = () => {
        this.setState({ showAttachments: true });
    };

    onHideAttachment = () => {
        this.setState({ showAttachments: false });
    };

    deleteAttachment = async (id: string) => {
        const { templates } = this.state;

        try {
            await deleteAttachment(id);
            this.setState({ templates: templates.filter((template) => template._id !== id) });
            track('ATTACHMENT_DELETED', { id: id });
        } catch (error) {
            notification.error({
                message: 'Failed to delete Template!',
                duration: 5,
                placement: 'topRight',
            });
            track('ATTACHMENT_DELETE_FAILED', {
                error: (error as Error).message,
                id: id,
            });
            console.error(error);
        }
    };

    setActiveTab = (tab: string) => {
        this.setState({ activeTab: tab as 'files' | 'template' });
    };

    render() {
        const {
            isVisible,
            onCancel,
            onClear,
            templateApplyText,
            templateApplyIcon,
            tabBarExtraContent,
        } = this.props;
        const { title, description, uploadState, link, activeTab, templates } = this.state;

        return (
            <div className="flex flex-col h-full">
                <Tabs
                    size="small"
                    tabBarGutter={4}
                    tabBarStyle={{
                        padding: '0 8px',
                        paddingTop: '8px',
                        background: '#F4F6F9',
                        marginBottom: '0px',
                    }}
                    style={{
                        height: '100%',
                    }}
                    className="attachment-tabs"
                    defaultActiveKey={activeTab}
                    onChange={this.setActiveTab}
                    tabBarExtraContent={tabBarExtraContent}
                >
                    <TabPane
                        className="h-full"
                        tab={<span className="px-8">SEND FILES</span>}
                        key="files"
                    >
                        <div className="flex-1 h-full">
                            <Form
                                name="basic"
                                labelCol={{ span: 8 }}
                                initialValues={{ remember: true }}
                                autoComplete="off"
                                style={{
                                    width: '100% !important',
                                }}
                                className="select-container h-full flex flex-col justify-between"
                            >
                                <div className="space-y-20 p-16">
                                    <div className="space-y-8">
                                        <p className="text-14 font-700 text-darwin-neutral-800">
                                            Title *
                                        </p>
                                        <Form.Item
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Please add Title!',
                                                },
                                            ]}
                                            name="title"
                                            initialValue={title}
                                        >
                                            <Input
                                                className="w-full text-14 rounded-6 border border-darwin-neutral-100 border-darwin-neutral-100 p-8"
                                                type="text"
                                                value={title}
                                                placeholder="Add your title here"
                                                onChange={this.onChangeTitle}
                                            ></Input>
                                        </Form.Item>
                                    </div>

                                    <div className="space-y-8">
                                        <p className="text-14 font-700 text-darwin-neutral-800">
                                            Note
                                        </p>

                                        <Form.Item name="note" initialValue={description}>
                                            <TextArea
                                                className="w-full text-14 rounded-6 border border-darwin-neutral-100 border-darwin-neutral-100 p-8"
                                                rows={4}
                                                value={description}
                                                placeholder="Add your note here"
                                                onChange={this.onChangeDescription}
                                            ></TextArea>
                                        </Form.Item>
                                    </div>

                                    <Form.Item name="attachment">
                                        <div className="space-y-8">
                                            <p className="text-14 font-700 text-darwin-neutral-800 mb-8">
                                                Attach file
                                            </p>

                                            <Upload
                                                id={`send-attachment-${randomString(8)}`}
                                                name="file"
                                                method="post"
                                                listType="text"
                                                accept="image/*,application/pdf"
                                                multiple={false}
                                                showUploadList={true}
                                                fileList={
                                                    link
                                                        ? [
                                                              {
                                                                  uid: this.fileId,
                                                                  name: link,
                                                                  url: link,
                                                              },
                                                          ]
                                                        : []
                                                }
                                                customRequest={async (e) => {
                                                    // this value is 3 MB = 3145728 Bytes
                                                    if (e.file as File) {
                                                        this.onFileChange(e.file as File);
                                                    } else {
                                                        notification.error({
                                                            message: 'File not Uploaded',
                                                            duration: 5,
                                                            placement: 'topRight',
                                                        });
                                                        track('ERROR', {
                                                            on: 'PatientPicModal',
                                                            what: 'patient picture is too large',
                                                        });
                                                    }
                                                    return true;
                                                }}
                                                onRemove={() => {
                                                    this.setState({ link: '', file_type: '' });
                                                }}
                                            >
                                                {!link && (
                                                    <Button
                                                        className="rounded-8"
                                                        icon={
                                                            <span className="mr-8 hover:#40a9ff">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 448 512"
                                                                    className="w-12 h-12 fill-actionPrimary"
                                                                >
                                                                    <path d="M246.6 9.4c-12.5-12.5-32.8-12.5-45.3 0l-128 128c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 109.3V320c0 17.7 14.3 32 32 32s32-14.3 32-32V109.3l73.4 73.4c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-128-128zM64 352c0-17.7-14.3-32-32-32s-32 14.3-32 32v64c0 53 43 96 96 96H352c53 0 96-43 96-96V352c0-17.7-14.3-32-32-32s-32 14.3-32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V352z" />
                                                                </svg>
                                                            </span>
                                                        }
                                                    >
                                                        Upload File
                                                    </Button>
                                                )}
                                            </Upload>
                                        </div>
                                    </Form.Item>
                                </div>

                                <Form.Item>
                                    <div className="flex items-center justify-between px-16 py-16 bg-darwin-neutral-50">
                                        <div>
                                            {onClear && (
                                                <button
                                                    className="flex px-12 py-7 secondary-btn btn--small cursor-pointer items-center"
                                                    onClick={onClear}
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>

                                        <div className="flex items-center space-x-8">
                                            <button
                                                className="flex px-12 py-7 secondary-btn btn--small cursor-pointer items-center"
                                                onClick={onCancel}
                                            >
                                                Cancel
                                            </button>

                                            <button
                                                className="space-x-4 inline-flex items-center focus:outline-none font-600 text-white rounded-4 hover:bg-actionPrimary-hover active:bg-actionPrimary-active disabled:bg-actionPrimary-disabled disabled:cursor-not-allowed bg-actionPrimary px-12 py-8 btn--small"
                                                type="submit"
                                                disabled={
                                                    title && uploadState !== 'uploading'
                                                        ? false
                                                        : true
                                                }
                                                onClick={this.onSendClick}
                                            >
                                                Send
                                            </button>
                                        </div>
                                    </div>
                                </Form.Item>
                            </Form>
                        </div>
                    </TabPane>
                    <TabPane tab={<span className="px-8">TEMPLATES</span>} key="template">
                        <AttachmentTemplateList
                            onSendTemplateClick={this.onSendTemplateClick}
                            onHideAttachment={this.onHideAttachment}
                            deleteAttachment={this.deleteAttachment}
                            templates={templates}
                            templateApplyText={templateApplyText}
                            templateApplyIcon={templateApplyIcon}
                        />
                    </TabPane>
                </Tabs>
            </div>
        );
    }
}

export default AttachmentModal;
